import { Controller } from "@hotwired/stimulus";

// A question in a questionnaire.
//
// A questionnaire-item knows what "value" it has given different question types,
// and when it's answered given various user actions. Show/hide decisions are
// owned by the questionnaire controller.
export default class extends Controller {
  static values = {
    type: String,
    key: String,
    answersOn: String,
    showIf: Object,
  };
  static targets = ["continue"];

  get key() {
    return this.keyValue;
  }

  get value() {
    switch (this.typeValue) {
      case "radio":
        return this.#getRadioValue();
      case "number":
        return this.#getNumberValue();
      case "money":
        return this.#getMoneyValue();
      case "checkbox":
      case "measures":
        return this.#getCheckboxValue();
      case "systems":
        return this.#getSystemsValue();
      default:
        return null;
    }
  }

  toggle(isShown) {
    isShown ? this.#expand() : this.#collapse();
  }

  get hasContinue() {
    return this.hasContinueTarget;
  }

  hideContinue() {
    if (!this.hasContinueTarget) return;
    this.continueTarget.classList.add("hidden");
  }

  isShown() {
    return !this.element.classList.contains("hidden");
  }

  hasValue() {
    return this.value !== null;
  }

  advance(event) {
    if (!["Tab", "Enter"].includes(event.key)) return;

    // Prevent submitting the whole form like Enter would do normally.
    event.preventDefault();

    this.syncMoneyDisplay();
    this.dispatch("advanced");
  }

  // Fires an input event on the money display field so the money-input
  // controller syncs whatever is shown (including the "0" default) to the
  // hidden cents field before the questionnaire advances.
  syncMoneyDisplay() {
    if (this.typeValue !== "money") return;
    const display = this.element.querySelector("[data-money-input-target='display']");
    if (display) display.dispatchEvent(new Event("input", { bubbles: true }));
  }

  focus() {
    if (!this.#input) return;

    this.#input.focus();
  }

  /* Private */

  // Use native querySelector() methods to select descendants, instead of
  // stimulus targets, as the targets can vary by question type.
  get #inputs() {
    return this.element.querySelectorAll("input");
  }

  get #input() {
    return this.element.querySelector("input:not([type='hidden']):not([type='range'])");
  }

  get #checkedInputs() {
    return this.element.querySelectorAll("input:checked");
  }

  get #hasCheckedInputs() {
    return this.#checkedInputs.length > 0;
  }

  // A nice alias.
  get #hasCheckedInput() {
    return this.#hasCheckedInputs;
  }

  get #checkedInput() {
    return this.#checkedInputs ? this.#checkedInputs[0] : null;
  }

  #expand() {
    this.#enable();
    this.#require();
    this.#toggleContinue();
    this.#show();
  }

  #collapse() {
    this.#hide();
    this.#disable();
  }

  #show() {
    this.element.classList.remove("hidden");
  }

  #hide() {
    this.element.classList.add("hidden");
  }

  #enable() {
    this.#inputs.forEach((i) => (i.disabled = false));
  }

  #disable() {
    this.#inputs.forEach((i) => (i.disabled = true));
  }

  #require() {
    this.#inputs.forEach((i) => {
      // Checkboxes are validated via hasValue() — the submit button stays
      // hidden until at least one is checked. Setting required on all of them
      // causes Chrome to block submission when any unchecked box is hidden.
      if (i.type !== "checkbox") i.required = true;
    });
  }

  #toggleContinue() {
    if (!this.hasContinueTarget) return;

    this.continueTarget.classList.toggle("hidden", this.hasValue());
  }

  #getRadioValue() {
    if (!this.#hasCheckedInput) return null;

    let value = this.#checkedInput.value;

    if (value === "true") {
      value = true;
    } else if (value === "false") {
      value = false;
    }

    return value;
  }

  #getNumberValue() {
    return this.#input.value == "" ? null : this.#input.value;
  }

  #getMoneyValue() {
    const input = Array.from(this.#inputs).find((i) => i.type !== "range");
    return !input || input.value === "" ? null : input.value;
  }

  #getCheckboxValue() {
    return this.#hasCheckedInputs ? Array.from(this.#checkedInputs, (i) => i.value) : null;
  }

  #getSystemsValue() {
    const ahriInputs = this.element.querySelectorAll("input[type='text']");
    return Array.from(ahriInputs).some((i) => i.value.trim() !== "") ? true : null;
  }
}
